// Clients del backoffice de `retail-catalog-business`: el lente (lecturas) y las 12 escrituras.
// `critical` y `list` pegan al mismo path con distinto `?filter=`; `getRotation` es otro endpoint.
import { apiFetch } from '../../../client';
import type {
    InventoryLensApi,
    InventoryLensCriticalResponseWire,
    InventoryLensListParams,
    InventoryLensListResponseWire,
    InventorySkuDetail,
    InventorySkuParams,
    InventoryWriteApi,
    InventoryWriteOptions,
    SkuPriceHistory,
    StoreRotationRow,
} from './contract';
import { toListView, toSkuLensView } from './inventoryLensMapper';

const BASE_URL = import.meta.env.VITE_API_RETAIL_CATALOG_BASE_URL;

const LENS_PATH = '/backoffice/inventory/lens';

/** Query del modo lista; sin `storeId` es el consolidado del retailer. */
export function listQuery({ period, storeId }: InventoryLensListParams): string {
    const qs = new URLSearchParams({ filter: 'list', period });
    if (storeId) qs.set('storeId', storeId);
    return qs.toString();
}

const skuPath = (sku: string) => `/backoffice/inventory/skus/${encodeURIComponent(sku)}`;

/** Query de la ficha; solo manda lo que viene. */
export function skuQuery({
    period,
    storeId,
    limit,
    nextToken,
    includeHolds,
}: InventorySkuParams): string {
    const qs = new URLSearchParams();
    if (period) qs.set('period', period);
    if (storeId) qs.set('storeId', storeId);
    if (limit !== undefined) qs.set('limit', String(limit));
    if (nextToken) qs.set('nextToken', nextToken);
    if (includeHolds) qs.set('includeHolds', 'true');
    const query = qs.toString();
    return query ? `?${query}` : '';
}

export const inventoryLensClient: InventoryLensApi = {
    getCritical: async () =>
        toSkuLensView(
            await apiFetch<InventoryLensCriticalResponseWire>(`${LENS_PATH}?filter=critical`, {
                baseUrl: BASE_URL,
            }),
        ),
    getList: async (params) =>
        toListView(
            await apiFetch<InventoryLensListResponseWire>(`${LENS_PATH}?${listQuery(params)}`, {
                baseUrl: BASE_URL,
            }),
        ),
    getRotation: () =>
        apiFetch<{ stores: StoreRotationRow[]; impossibleDates?: number }>(
            '/backoffice/inventory/rotation',
            { baseUrl: BASE_URL },
        ),
    getSku: (sku, params) =>
        apiFetch<InventorySkuDetail>(`${skuPath(sku)}${skuQuery(params)}`, { baseUrl: BASE_URL }),
    listSkuPrices: (sku) =>
        apiFetch<SkuPriceHistory>(`${skuPath(sku)}/prices`, { baseUrl: BASE_URL }),
};

const TRANSFERS_PATH = '/backoffice/inventory/transfers';

const transferPath = (transferId: string, action: 'receive' | 'cancel') =>
    `${TRANSFERS_PATH}/${encodeURIComponent(transferId)}/${action}`;

/** Escritura idempotente: la llave del intento viaja en `Idempotency-Key`. */
function write<T>(
    path: string,
    method: 'POST' | 'PATCH',
    { idempotencyKey }: InventoryWriteOptions,
    body?: unknown,
): Promise<T> {
    return apiFetch<T>(path, {
        baseUrl: BASE_URL,
        method,
        headers: { 'Idempotency-Key': idempotencyKey },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
}

export const inventoryWriteClient: InventoryWriteApi = {
    createProduct: (body, options) => write('/backoffice/catalog/products', 'POST', options, body),
    createKit: (body, options) => write('/backoffice/catalog/kits', 'POST', options, body),
    createService: (body, options) => write('/backoffice/catalog/services', 'POST', options, body),
    updateProduct: (sku, body, options) => write(skuPath(sku), 'PATCH', options, body),
    deactivateProduct: (sku, options) => write(`${skuPath(sku)}/deactivate`, 'POST', options),
    createSkuPrice: (sku, body, options) => write(`${skuPath(sku)}/prices`, 'POST', options, body),
    registerEntry: (body, options) => write('/backoffice/inventory/entries', 'POST', options, body),
    registerAdjustment: (body, options) =>
        write('/backoffice/inventory/adjustments', 'POST', options, body),
    createTransfer: (body, options) => write(TRANSFERS_PATH, 'POST', options, body),
    receiveTransfer: (transferId, options) =>
        write(transferPath(transferId, 'receive'), 'POST', options),
    cancelTransfer: (transferId, options) =>
        write(transferPath(transferId, 'cancel'), 'POST', options),
    listTransfers: (state) =>
        apiFetch(
            state
                ? `${TRANSFERS_PATH}?${new URLSearchParams({ state }).toString()}`
                : TRANSFERS_PATH,
            {
                baseUrl: BASE_URL,
            },
        ),
};
